import { LuX } from '@react-icons/all-files/lu/LuX'
import React, { useEffect } from 'react'
import type { Location } from 'react-router-dom'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { getOrFetchTrack } from '@disco/state-management/actions/PlaylistTrackActions'
import { getRoutingPreviousLocation } from '@disco/state-management/selectors/routing'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { getModalBestReturnTo } from '@disco/utils/modals'

import type { TrackId } from 'types/track'

import DocumentHeadTags from 'components/document-head-tags'
import { Dialog, Flex, Grid, IconButton, Skeleton, Text } from 'components/ds'
import TrackStatsModal from 'components/modals/track/stats'

import { useDispatch, useSelector } from 'hooks/use-redux'

const TrackStats = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ trackId: string }>()
  const trackIdStr = params.trackId!
  const trackId = parseInt(trackIdStr, 10) as TrackId
  const track = useSelector(state =>
    getTrack(state, {
      id: trackId,
    }),
  )
  const previousLocation: Location | null | undefined = useSelector(
    getRoutingPreviousLocation,
  )

  const onClose = () => {
    const returnTo = getModalBestReturnTo(location, previousLocation)
    navigate(returnTo)
  }

  useEffect(() => {
    if (!track) {
      dispatch(getOrFetchTrack(trackId))
    }
  }, [])

  if (!track) {
    return (
      <>
        <DocumentHeadTags title="Track stats | DISCO" />
        <Dialog.Root open onOpenChange={open => !open && onClose()}>
          <Dialog.Content maxWidth="780px" aria-describedby={undefined}>
            <Flex justify="between" align="start">
              <Text size="1" weight="bold" color="gray">
                TRACK STATS
              </Text>
              <Dialog.Close>
                <IconButton
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  aria-label="Close"
                >
                  <LuX size={18} />
                </IconButton>
              </Dialog.Close>
            </Flex>
            <Dialog.Title mt="1" mb="4">
              <Skeleton>Track name</Skeleton>
            </Dialog.Title>
            <Grid columns="4" gap="3" mb="5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height="120px" />
              ))}
            </Grid>
            <Skeleton height="var(--font-size-1)" width="120px" mb="3" />
            <Flex direction="column" gap="3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height="40px" />
              ))}
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </>
    )
  }

  return (
    <>
      <DocumentHeadTags title="Track stats | DISCO" />
      <TrackStatsModal track={track} onClose={onClose} />
    </>
  )
}

export default TrackStats
